/**
 * An item in the SAVE request's shape → the draft's. The way a proposal's after-states (ai-plan-builder
 * R15) enter the tree: an exercise Buddy added, replaced or changed, a day, a phase.
 *
 * ── One reading of an item, however it arrived ───────────────────────────────────────────────
 * The save shape is turned into the full read's shape and handed to `responseToDraft`'s own
 * readers, rather than mapped into the draft a second time. Every rule those readers keep — the base
 * cycle, the override coordinates, the trimmed group and tip, the verbatim rule map, the rest-column
 * derivation — then holds for a proposal's item exactly as for a loaded one, and a field added to the
 * reader reaches both.
 *
 * ── What the save shape does not carry ───────────────────────────────────────────────────────
 * An exercise's name, its type and its measurement contract are library facts, and the save request
 * names an exercise by id alone. They arrive as the `ExercisePick` the library row gives, the same
 * shape a library insert seeds from. Without the contract the set table would offer no column and
 * the serialiser would drop the AMRAP caps, so a pick is required rather than optional.
 *
 * The base sets are tagged with the exercise's base cycle and the overrides keep their absolute
 * coordinates, which is the shape `authoredSets` and `readOverrides` read; an override standing on the
 * base cycle is the base itself and is not sent twice.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type {
  TrainWithMeModulesPlansDtosRequestsPostDayTemplateInput,
  TrainWithMeModulesPlansDtosRequestsPostExerciseTemplateInput,
  TrainWithMeModulesPlansDtosRequestsPostPhaseTemplateInput,
  TrainWithMeModulesPlansDtosResponsesDayTemplateResponse,
  TrainWithMeModulesPlansDtosResponsesExerciseTemplateResponse,
} from '~/shared/api/generated/model';

import type { ExercisePick } from './draftFactory';
import type { DayDraft, ExerciseDraft, PhaseDraft } from './planDraft.types';
import { dayToDraft, exerciseToDraft, phaseToDraft } from './responseToDraft';

type ExerciseInput = TrainWithMeModulesPlansDtosRequestsPostExerciseTemplateInput;
type DayInput = TrainWithMeModulesPlansDtosRequestsPostDayTemplateInput;
type PhaseInput = TrainWithMeModulesPlansDtosRequestsPostPhaseTemplateInput;
type ExerciseResponse = TrainWithMeModulesPlansDtosResponsesExerciseTemplateResponse;
type DayResponse = TrainWithMeModulesPlansDtosResponsesDayTemplateResponse;

/** The library row behind every exercise id an item names. */
export type ExercisePicks = ReadonlyMap<number, ExercisePick>;

function exerciseAsRead(input: ExerciseInput, pick: ExercisePick, activationCycle: number | null, baseCycle: number): ExerciseResponse {
  return {
    exerciseId: input.exerciseId,
    exerciseName: pick.name,
    exerciseType: pick.typeKey,
    measurementFields: [...pick.measurementFields],
    slotToken: input.slotToken,
    role: input.role,
    group: input.group,
    notes: input.notes,
    progressionRules: input.progressionRules,
    realMaxMultiplier: input.realMaxMultiplier,
    realMaxSource: input.realMaxSource,
    retirementCycle: input.retirementCycle,
    excludedCycles: input.excludedCycles,
    prescriptionMode: input.prescriptionMode,
    ...(activationCycle === null ? {} : { activationCycle }),
    templateSets: [
      ...input.sets.map((set, index) => ({ ...set, setIndex: index + 1, cycleIndex: baseCycle })),
      ...(input.setOverrides ?? [])
        .filter((override) => override.cycleIndex !== baseCycle)
        .map((override) => ({ ...override, isManuallyOverridden: true })),
    ],
  };
}

/** Every exercise of a day in the read's shape, or `null` when one names an id with no pick. */
function exercisesAsRead(input: DayInput, picks: ExercisePicks, phaseStartCycle: number): ExerciseResponse[] | null {
  const read: ExerciseResponse[] = [];
  for (const exercise of input.sessions?.[0]?.exercises ?? []) {
    const pick = picks.get(exercise.exerciseId);
    if (pick === undefined) return null;
    read.push(exerciseAsRead(exercise, pick, null, phaseStartCycle));
  }
  return read;
}

function dayAsRead(input: DayInput, exercises: ExerciseResponse[]): DayResponse {
  const session = input.sessions?.[0];
  return {
    slotToken: input.slotToken,
    name: input.name,
    dayOfWeek: input.dayOfWeek,
    restDaysAfter: input.restDaysAfter,
    activationCycle: input.activationCycle,
    retirementCycle: input.retirementCycle,
    excludedCycles: input.excludedCycles,
    sessions: [
      {
        name: session?.name,
        notes: session?.notes,
        estimatedDurationMinutes: session?.estimatedDurationMinutes,
        defaultRestTimeSeconds: session?.defaultRestTimeSeconds,
        exercises,
      },
    ],
  };
}

/**
 * A NEW day — one no server has stored. `liveClosedCycles` is `[]` for the reason `createDay` gives:
 * the client has closed no cycle of a day that did not exist a moment ago.
 */
function newDay(day: DayDraft): DayDraft {
  return { ...day, liveClosedCycles: [] };
}

/**
 * One exercise. `activationCycle` is the slot's own when the item is a change to an exercise already
 * in the draft (the slot and its history stay), and `null` for a new slot, which bases on its phase.
 */
export function exerciseFromSaveShape(
  input: ExerciseInput,
  pick: ExercisePick,
  activationCycle: number | null,
  phaseStartCycle: number,
): ExerciseDraft {
  return exerciseToDraft(exerciseAsRead(input, pick, activationCycle, activationCycle ?? phaseStartCycle), phaseStartCycle);
}

/** A new day with its exercises, or `null` when one of them names an id with no pick. */
export function dayFromSaveShape(input: DayInput, picks: ExercisePicks, phaseStartCycle: number): DayDraft | null {
  const exercises = exercisesAsRead(input, picks, phaseStartCycle);
  return exercises === null ? null : newDay(dayToDraft(dayAsRead(input, exercises), phaseStartCycle));
}

/** A new phase starting at `startCycle`, or `null` when one of its exercises names an id with no pick. */
export function phaseFromSaveShape(input: PhaseInput, picks: ExercisePicks, startCycle: number): PhaseDraft | null {
  const days: DayResponse[] = [];
  for (const day of input.days ?? []) {
    const exercises = exercisesAsRead(day, picks, startCycle);
    if (exercises === null) return null;
    days.push(dayAsRead(day, exercises));
  }
  const phase = phaseToDraft({
    name: input.name,
    phaseType: input.phaseType,
    cycleCount: input.cycleCount,
    startCycle,
    days,
  });
  return { ...phase, days: phase.days.map(newDay) };
}
